import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, Eye, X, Trash2 } from "lucide-react";
import api, { adminReq } from "../../lib/api";
import { toast } from "sonner";
import useSelection from "../../hooks/useSelection";
import SelectionBar from "../../components/SelectionBar";
import CheckboxCell from "../../components/CheckboxCell";
import { ConfirmModal } from "./Trash";

export default function AdminDomestic({ rows, onRefresh }) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const filtered = useMemo(
    () =>
      (rows || []).filter((r) => {
        if (!q) return true;
        return [r.first_name, r.last_name, r.email, r.program_title, r.campus]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q.toLowerCase()));
      }),
    [rows, q]
  );

  const sel = useSelection(filtered);

  const bulkDelete = async (ids) => {
    try {
      await api.post(
        "/admin/domestic/bulk-delete",
        { ids },
        adminReq()
      );
      toast.success(`Moved ${ids.length} to trash`);
      sel.clear();
      if (onRefresh) onRefresh();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, program, campus..."
            className="bg-transparent outline-none text-sm flex-1"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 text-left text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 w-10">
                  <CheckboxCell
                    checked={sel.allSelected}
                    indeterminate={sel.someSelected}
                    onChange={sel.toggleAll}
                    ariaLabel="Select all"
                  />
                </th>
                <th className="px-4 py-3">Applicant</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Program</th>
                <th className="px-4 py-3">Campus</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center text-slate-400 text-sm py-12"
                  >
                    No domestic applications
                  </td>
                </tr>
              ) : (
                filtered.map((r, idx) => {
                  const checked = sel.isSelected(r.id);
                  return (
                    <motion.tr
                      key={r.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.02, duration: 0.25 }}
                      onClick={() => sel.toggle(r.id)}
                      className={`border-t border-slate-100 cursor-pointer ${
                        checked ? "bg-amber-50/60" : "hover:bg-slate-50/70"
                      }`}
                    >
                      <td
                        className="px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <CheckboxCell
                          checked={checked}
                          onChange={() => sel.toggle(r.id)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C49A3A] to-[#E0BB5C] text-white flex items-center justify-center font-semibold text-xs">
                            {(r.first_name || "?")
                              .slice(0, 1)
                              .toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-slate-800">
                              {r.first_name} {r.last_name}
                            </div>
                            <div className="text-xs text-slate-400">
                              {r.city}, {r.province}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.email}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">
                        {r.program_title}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.campus}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {new Date(r.created_at).toLocaleDateString()}
                      </td>
                      <td
                        className="px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelected(r)}
                            className="inline-flex items-center gap-1 text-cct-blue text-xs font-semibold hover:underline"
                          >
                            <Eye className="w-3.5 h-3.5" /> View
                          </button>
                          <button
                            onClick={() => setConfirm({ ids: [r.id] })}
                            className="inline-flex items-center gap-1 text-red-500 text-xs font-semibold hover:underline"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SelectionBar
        count={sel.count}
        onClear={sel.clear}
        noun="application"
        actions={[
          {
            label: "Move to Trash",
            icon: Trash2,
            variant: "danger",
            onClick: () => setConfirm({ ids: sel.selectedIds }),
          },
        ]}
      />

      {confirm && (
        <ConfirmModal
          title="Move to Trash?"
          message={`${confirm.ids.length} application${
            confirm.ids.length !== 1 ? "s" : ""
          } will be moved to Trash. You can restore them later.`}
          confirmLabel="Move to Trash"
          danger
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            bulkDelete(confirm.ids);
            setConfirm(null);
          }}
        />
      )}

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
        >
          <motion.div
            initial={{ x: 400 }}
            animate={{ x: 0 }}
            transition={{ type: "spring", damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl"
          >
            <div className="p-6 border-b border-slate-200 flex justify-between items-start sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gold font-semibold">
                  Domestic Application
                </div>
                <h3 className="font-display text-2xl font-semibold text-cct-blue mt-1">
                  {selected.first_name} {selected.last_name}
                </h3>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-2 text-sm">
              {Object.entries(selected)
                .filter(([k]) => !(["_id", "id", "deleted_at"].includes(k)))
                .map(([k, v]) => (
                  <div
                    key={k}
                    className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100"
                  >
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                      {k.replace(/_/g, " ")}
                    </div>
                    <div className="col-span-2 text-slate-800 break-words">
                      {typeof v === "boolean"
                        ? v
                          ? "Yes"
                          : "No"
                        : String(v ?? "—")}
                    </div>
                  </div>
                ))}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
